import { useCallback, useEffect, useRef, useState } from "react";
import { WandSparkles, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EnhancePromptDiff } from "./EnhancePromptDiff";

export const ENHANCE_MIN_CHARS = 10;
export const ENHANCE_MAX_CHARS = 8000;

type EnhanceState = "idle" | "loading" | "diff" | "applied";

export interface EnhancePromptButtonProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Invocado quando um refinamento conclui — para telemetria externa. */
  onEnhanced?: (meta: {
    status: "applied" | "discarded" | "error";
    durationMs: number;
    model: string;
  }) => void;
}

export function EnhancePromptButton({
  value,
  onChange,
  disabled,
  onEnhanced,
}: EnhancePromptButtonProps) {
  const [state, setState] = useState<EnhanceState>("idle");
  const [refined, setRefined] = useState("");
  const [original, setOriginal] = useState("");
  const [showUndo, setShowUndo] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const undoTimeoutRef = useRef<number | null>(null);

  const enhanceMutation = trpc.prompt.enhance.useMutation();

  const trimmed = value.trim();
  const belowMin = trimmed.length < ENHANCE_MIN_CHARS;
  const aboveMax = trimmed.length > ENHANCE_MAX_CHARS;
  const isDisabled = disabled === true || belowMin || aboveMax || state === "loading";

  const tooltipLabel = belowMin
    ? `Escreva ao menos ${ENHANCE_MIN_CHARS} caracteres`
    : aboveMax
      ? "Texto muito longo, refine manualmente"
      : "Refinar prompt (Cmd+K)";

  const handleEnhance = useCallback(async () => {
    if (isDisabled) return;
    setErrorMessage(null);
    setOriginal(value);
    setState("loading");
    try {
      const result = await enhanceMutation.mutateAsync({
        text: trimmed,
        model: "haiku",
      });
      setRefined(result.refined);
      setState("diff");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Nao foi possivel refinar o prompt";
      setErrorMessage(message);
      setState("idle");
      onEnhanced?.({
        status: "error",
        durationMs: 0,
        model: "haiku",
      });
    }
  }, [enhanceMutation, isDisabled, onEnhanced, trimmed, value]);

  const handleApply = useCallback(
    (finalText: string) => {
      onChange(finalText);
      setState("applied");
      setShowUndo(true);
      onEnhanced?.({
        status: "applied",
        durationMs: enhanceMutation.data?.durationMs ?? 0,
        model: enhanceMutation.data?.model ?? "haiku",
      });
      if (undoTimeoutRef.current) window.clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = window.setTimeout(() => {
        setShowUndo(false);
        setState("idle");
      }, 10_000);
    },
    [enhanceMutation.data, onChange, onEnhanced],
  );

  const handleDiscard = useCallback(() => {
    setState("idle");
    setRefined("");
    onEnhanced?.({
      status: "discarded",
      durationMs: enhanceMutation.data?.durationMs ?? 0,
      model: enhanceMutation.data?.model ?? "haiku",
    });
  }, [enhanceMutation.data, onEnhanced]);

  const handleUndo = useCallback(() => {
    onChange(original);
    setShowUndo(false);
    setState("idle");
    if (undoTimeoutRef.current) window.clearTimeout(undoTimeoutRef.current);
  }, [onChange, original]);

  // Listener de shortcut global (Cmd+K / Ctrl+K) quando o textarea pai tem foco.
  // Exposto via ref imperativo nao eh necessario: ChatPanel ja escuta keydown
  // e invoca este handler por ref. Em vez disso, deixamos a integracao controlar.
  // Para uso standalone (storybook), exponha um listener local:
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isShortcut =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (!isShortcut) return;
      // Se o alvo nao for um input/textarea/contenteditable, ignora
      const target = e.target as HTMLElement | null;
      const isEditable =
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement ||
        (target?.isContentEditable ?? false);
      if (!isEditable) return;
      e.preventDefault();
      void handleEnhance();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleEnhance]);

  useEffect(
    () => () => {
      if (undoTimeoutRef.current) window.clearTimeout(undoTimeoutRef.current);
    },
    [],
  );

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={handleEnhance}
              disabled={isDisabled}
              aria-label="Refinar prompt com IA (Cmd+K)"
              aria-keyshortcuts="Control+K"
              aria-busy={state === "loading"}
              data-testid="enhance-prompt-button"
              data-state={state}
            >
              {state === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <WandSparkles className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">{tooltipLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {state === "diff" && (
        <EnhancePromptDiff
          before={original}
          after={refined}
          onApply={handleApply}
          onDiscard={handleDiscard}
        />
      )}

      {showUndo && state === "applied" && (
        <div
          className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-lg"
          role="status"
          aria-live="polite"
          data-testid="enhance-undo-toast"
        >
          <span className="text-xs text-foreground">Prompt refinado</span>
          <button
            type="button"
            onClick={handleUndo}
            className="text-xs font-medium text-primary underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Desfazer
          </button>
        </div>
      )}

      {errorMessage && (
        <div
          className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-full border border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-destructive"
          role="alert"
        >
          {errorMessage}
        </div>
      )}
    </>
  );
}
